export const ERROR_MESSAGE_KEYS = {
  NOT_FOUND: 'errorMessages.notFound',
  CLIENT_ERROR: 'errorMessages.clientError',
  SERVER_ERROR: 'errorMessages.serverError',
  GENERAL_ERROR: 'errorMessages.generalError',
} as const;

export type ErrorMessageKey = (typeof ERROR_MESSAGE_KEYS)[keyof typeof ERROR_MESSAGE_KEYS];

const NOT_FOUND_STATUS = 404;
const CLIENT_STATUS_PREFIX = '4';
const SERVER_STATUS_PREFIX = '5';

const STATUS_FROM_MESSAGE_REGEXP = /status code (\d{3})/i;

const toStatusNumber = (value: unknown): number | null => {
  const status = typeof value === 'string' ? Number(value) : value;
  return typeof status === 'number' && Number.isInteger(status) && status > 0 ? status : null;
};

export const getErrorStatus = (error?: unknown): number | null => {
  if (error === null || error === undefined) {
    return null;
  }

  const directStatus = toStatusNumber(error);
  if (typeof error === 'number' || typeof error === 'string') {
    return directStatus;
  }

  if (typeof error !== 'object') {
    return null;
  }

  const candidate = error as {
    response?: { status?: unknown; statusCode?: unknown };
    request?: { status?: unknown };
    status?: unknown;
    statusCode?: unknown;
    message?: unknown;
  };

  const status =
    toStatusNumber(candidate.response?.status) ??
    toStatusNumber(candidate.response?.statusCode) ??
    toStatusNumber(candidate.status) ??
    toStatusNumber(candidate.statusCode) ??
    toStatusNumber(candidate.request?.status);

  if (status !== null) {
    return status;
  }

  if (typeof candidate.message === 'string') {
    const matched = candidate.message.match(STATUS_FROM_MESSAGE_REGEXP);
    if (matched) {
      return toStatusNumber(matched[1]);
    }
  }

  return null;
};

export const getErrorMessageKey = (error?: unknown): ErrorMessageKey => {
  const status = getErrorStatus(error);

  if (status === null) {
    return ERROR_MESSAGE_KEYS.GENERAL_ERROR;
  }

  if (status === NOT_FOUND_STATUS) {
    return ERROR_MESSAGE_KEYS.NOT_FOUND;
  }

  const statusAsText = String(status);

  if (statusAsText.startsWith(CLIENT_STATUS_PREFIX)) {
    return ERROR_MESSAGE_KEYS.CLIENT_ERROR;
  }

  if (statusAsText.startsWith(SERVER_STATUS_PREFIX)) {
    return ERROR_MESSAGE_KEYS.SERVER_ERROR;
  }

  return ERROR_MESSAGE_KEYS.GENERAL_ERROR;
};
